import { decimalFormatter } from "@/utils/utils";
import {
  Severity,
  valuesExist,
  type ValidationRuleGenerator
} from "../scenarioValidator";
import {
  MAX_GAS_RATE_BEFORE_SEPARATION,
  TURPIN_MAX,
  VISCOSITY_MAX
} from "../validatorConstants";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";

export const intakeConditionChecksNames = [
  "pip_range",
  "target_rate_above_max_flow_rate",
  "liquid_above_pump",
  "gas_ratio_before_separation",
  "static_fluid_level_above_surface",
  "producing_fluid_level_above_surface",
  "gas_separation_max_flow_exceeded",
  "turpin_coefficient",
  "viscosity"
] as const;

export const intakeConditionChecks: ValidationRuleGenerator = ({
  calcs,
  parameters
}) => [
  // PIP check
  {
    name: "pip_range",
    message: `Liquid level below pump intake`,
    severity: Severity.error,
    isActive: valuesExist([
      calcs.pumpIntakePressurePSIA.value,
      parameters.value?.bottom_casing_pressure_psia
    ]),
    isInvalid:
      calcs.intakeCalculations.aggregate.liquidAbovePumpTvdFt.value < 0,
    disablesRules: ["liquid_above_pump"],
    rulePlacement: EquipmentCalculatorSectionKey.PUMP
  },
  // target above max flow rate check
  {
    name: "target_rate_above_max_flow_rate",
    message: `Target flow rate exceeds maximum flow rate of well`,
    severity: Severity.error,
    isActive: valuesExist([
      calcs.maxFlowRateBBLD.value,
      parameters.value?.target_liquid_rate_std_barrel_day
    ]),
    isInvalid:
      parameters.value?.target_liquid_rate_std_barrel_day >
      calcs.maxFlowRateBBLD.value,
    rulePlacement: EquipmentCalculatorSectionKey.PUMP
  },

  // Liquid above pump check
  {
    name: "liquid_above_pump",
    message: `Insufficient liquid above pump`,
    severity: Severity.warn,
    isActive: valuesExist([
      calcs.pumpIntakePressurePSIA.value,
      parameters.value?.bottom_casing_pressure_psia
    ]),
    isInvalid:
      calcs.intakeCalculations.aggregate.liquidAbovePumpTvdFt.value < 20,
    rulePlacement: EquipmentCalculatorSectionKey.PUMP
  },

  // Gas Ratio check
  {
    name: "gas_ratio_before_separation",
    message: `Free gas before separation is greater than ${Math.floor(
      MAX_GAS_RATE_BEFORE_SEPARATION * 100
    )}%`,
    severity: Severity.warn,
    isActive: valuesExist([
      calcs.fluidProperties.fluidPropertiesAtIntake.value.gasFlowVolumeRatio
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtIntake.value.gasFlowVolumeRatio >
      MAX_GAS_RATE_BEFORE_SEPARATION,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },

  {
    name: "static_fluid_level_above_surface",
    message: `Static Well Test Fluid Level is ${decimalFormatter(
      -1 * calcs.basicData.value.staticFluidLevelFeet
    )} ft above surface`,
    severity: Severity.warn,
    isActive: valuesExist([calcs.basicData.value.staticFluidLevelFeet]),
    isInvalid: calcs.basicData.value.staticFluidLevelFeet < 0,
    rulePlacement: EquipmentCalculatorSectionKey.WELL_TEST
  },

  {
    name: "producing_fluid_level_above_surface",
    message: `Producing Well Test Fluid Level is ${decimalFormatter(
      -1 * calcs.basicData.value.producingFluidLevelFeet
    )} ft above surface`,
    severity: Severity.warn,
    isActive: valuesExist([calcs.basicData.value.producingFluidLevelFeet]),
    isInvalid: calcs.basicData.value.producingFluidLevelFeet < 0,
    rulePlacement: EquipmentCalculatorSectionKey.WELL_TEST
  },

  // Gas Separation Efficiency check
  {
    name: "gas_separation_max_flow_exceeded",
    message: "Max flow for Gas Separation has been exceeded.",
    severity: Severity.warn,
    isActive:
      valuesExist([
        calcs.fluidProperties.fluidPropertiesAtIntake.value
          .liquidFlowRateBBLD || 0
      ]) && calcs.orderedIntakes.value.length > 0,
    isInvalid:
      (calcs.fluidProperties.fluidPropertiesAtIntake.value.liquidFlowRateBBLD ||
        0) >
      (-1 * calcs.primaryIntake.value?.r_intake?.eff1) /
        calcs.primaryIntake.value?.r_intake?.eff2,
    affectedXrefIds: calcs.primaryIntake.value?.id,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },

  // Turpin coefficient check
  {
    name: "turpin_coefficient",
    message: `Turpin Coefficient great than ${TURPIN_MAX}`,
    severity: Severity.warn,
    isActive: valuesExist([
      calcs.intakeCalculations.aggregate.turpinCoefficient
    ]),
    isInvalid:
      calcs.intakeCalculations.aggregate.turpinCoefficient.value > TURPIN_MAX,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },

  // Max viscosity check
  {
    name: "viscosity",
    message: `Calculated Viscosity > ${VISCOSITY_MAX} cP. Consider pump derating`,
    severity: Severity.warn,
    isActive: valuesExist([
      calcs.fluidProperties.fluidPropertiesAtIntake.value.liquidViscosityCP
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtIntake.value.liquidViscosityCP >
      VISCOSITY_MAX,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  }
];
